import * as vp from 'vopay';
import { Box, Title, Select, CardTabs } from 'vopay';
import { Button, TableFiltersContainer, Table } from 'UI';

import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import useFetchTickets from '@/hooks/useApi/useFetchTickets';
import useFetchUsers from '@/hooks/useApi/useFetchUsers';
import { usePartnerData } from '@/providers/PartnerData.provider';
import { useTranslation } from 'react-i18next';

type UserOptions = {
  label: string;
  value: string;
}[];

const TicketsList = (): React.JSX.Element => {
  const { t } = useTranslation('translations', { keyPrefix: 'Tickets.List' });
  const history = useHistory();
  type TabContents = {
    active: React.JSX.Element;
    closed: React.JSX.Element;
  };
  const [tickets, getTickets] = useFetchTickets({});
  const [activeTab, setActiveTab] = useState<keyof TabContents>('active');
  const [userTicket, setUserTicket] = useState('');

  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  const handleTabChange = (value: string) => {
    if (value === 'active' || value === 'closed') {
      setActiveTab(value);
    }
  };

  useEffect(() => {
    getTickets();
  }, []);

  const { users, fetchUsers } = useFetchUsers();

  useEffect(() => {
    // The hook has logged a failure; the filter then offers All Users only, as before.
    fetchUsers().catch(() => {});
  }, []);

  useEffect(() => {
    getTickets({
      Email: '',
    });
  }, [activeTab]);

  const [userOptions, setUserOptions] = useState<UserOptions>([]);

  useEffect(() => {
    const userList = users.map((user) => ({ label: user.Username, value: user.Username }));
    setUserOptions([{ label: t('allUsersOptions', { defaultValue: 'All Users' }), value: 'All' }, ...userList]);
  }, [users, t]);

  const handleFilterSelect = (user: string) => {
    if (user !== null && user !== '') {
      user = user == 'All' ? '' : user;
      setUserTicket(user);

      getTickets({
        Email: user,
      });
    }
  };

  const activeTickets: vp.TableProps<vp.Ticket> = {
    id: 'tickets-list',
    version: '2',
    zebra: true,
    columns: [
      {
        id: 'TicketId',
        header: t('Table.ticketId'),
        align: 'left',
        template: ({ data }) => <Table.Link to={{ pathname: `tickets/${data.id}` }}>{data.id}</Table.Link>,
      },
      {
        id: 'status',
        header: t('Table.status'),
        align: 'left',
        field: 'status',
      },
      {
        id: 'subject',
        header: t('Table.subject'),
        align: 'left',
        field: 'subject',
      },
      {
        id: 'dateAdded',
        header: t('Table.dateAdded'),
        align: 'left',
        field: 'dateAdded',
      },
      {
        id: 'dateUpdated',
        header: t('Table.dateUpdated'),
        align: 'left',
        field: 'lastUpdated',
      },
    ],
    rows:
      Object.values(tickets?.Tickets || {})
        .filter(
          (ticket) =>
            ticket.id !== null &&
            ticket.status?.toLowerCase() !== 'solved' &&
            ticket.status?.toLowerCase() !== 'closed',
        )
        .map((ticket) => ({
          id: ticket.id?.toString() || '',
          data: ticket,
        })) || [],
  };

  const closedTickets: vp.TableProps<vp.Ticket> = {
    id: 'tickets-list',
    version: '2',
    zebra: true,
    columns: [
      {
        id: 'TicketId',
        header: t('Table.ticketId'),
        align: 'left',
        template: ({ data }) => <Table.Link to={{ pathname: `tickets/${data.id}` }}>{data.id}</Table.Link>,
      },
      {
        id: 'status',
        header: t('Table.status'),
        align: 'left',
        field: 'status',
      },
      {
        id: 'subject',
        header: t('Table.subject'),
        align: 'left',
        field: 'subject',
      },
      {
        id: 'dateAdded',
        header: t('Table.dateAdded'),
        align: 'left',
        field: 'dateAdded',
      },
      {
        id: 'dateUpdated',
        header: t('Table.dateUpdated'),
        align: 'left',
        field: 'lastUpdated',
      },
    ],
    rows:
      Object.values(tickets?.Tickets || {})
        .filter(
          (ticket) =>
            ticket.id !== null &&
            (ticket.status?.toLowerCase() === 'solved' || ticket.status?.toLowerCase() === 'closed'),
        )
        .map((ticket) => ({
          id: ticket.id?.toString() || '',
          data: ticket,
        })) || [],
  };

  return (
    <div>
      <Box flx="col" gapSize="36px">
        <Box jc="sb">
          <Title title={t('pageTitle')} />

          <Button
            id="tickets-addTicket"
            colorScheme="primary"
            disabled={isRoleViewOnly}
            onClick={() => history.push('/tickets/create')}
          >
            {t('submitTicketButton')}
          </Button>
        </Box>

        <CardTabs.Root value={activeTab} onValueChange={handleTabChange}>
          <CardTabs.List aria-label={t('tabsAriaLabel', { defaultValue: 'Ticket List' })}>
            <CardTabs.Trigger value="active">{t('activeTab')}</CardTabs.Trigger>
            <CardTabs.Trigger value="closed">{t('closedTab')}</CardTabs.Trigger>
          </CardTabs.List>

          {['active', 'closed'].map((tab) => (
            <CardTabs.Content key={tab} value={tab}>
              <Box flx="col" gap="sm">
                <TableFiltersContainer filtersApplied={!!userTicket}>
                  <TableFiltersContainer.Trigger />

                  <TableFiltersContainer.Content>
                    <Box>
                      <Box width="200px">
                        <Select
                          version="2"
                          options={userOptions}
                          placeholder={t('userOptionsPlaceholder')}
                          name="ticket-filter"
                          value={userTicket}
                          onBlur={() => {}}
                          onChange={(e) => {
                            handleFilterSelect(e.target.value);
                          }}
                        />
                      </Box>
                    </Box>
                  </TableFiltersContainer.Content>
                </TableFiltersContainer>
              </Box>

              <Box>
                {tab === 'active' && <Table {...activeTickets} />}
                {tab === 'closed' && <Table {...closedTickets} />}
              </Box>
            </CardTabs.Content>
          ))}
        </CardTabs.Root>
      </Box>
    </div>
  );
};

export default TicketsList;
